// The Invite create drawer (docs/09 §4, UI-LAW §4a Drawer):
// deep-linked by the CALLER via `?new=1` — this component renders only while
// the param is set and the caller can manage, so field state resets on close
// by unmount. The Email field is the panel's first focusable element, which
// the Drawer focus trap targets on open.

import type { SyntheticEvent } from "react";
import { useState } from "react";

import type { components } from "../../api/schema";
import {
  Button,
  Drawer,
  Inline,
  InlineError,
  Select,
  Stack,
  Text,
  TextInput,
} from "../../ui";

type InvitationCreate = components["schemas"]["InvitationCreate"];
type MemberRole = components["schemas"]["MemberRole"];
type Team = components["schemas"]["TeamResponse"];

export interface InviteDrawerProps {
  // Blocking create failure — stays inline in the drawer (UI-LAW §4a: a toast
  // is never the sole surface for a blocking error).
  createErrorMessage?: string;
  // Ceiling-capped role options from `useCallerAuthority` (docs/05 §6.4).
  grantableRoles: MemberRole[];
  isCreatePending: boolean;
  isTeamsPending: boolean;
  onDismiss: () => void;
  onSubmit: (input: InvitationCreate) => void;
  teams: Team[];
  teamsErrorMessage?: string;
}

export function InviteDrawer({
  createErrorMessage,
  grantableRoles,
  isCreatePending,
  isTeamsPending,
  onDismiss,
  onSubmit,
  teams,
  teamsErrorMessage,
}: InviteDrawerProps) {
  const [email, setEmail] = useState("");
  const [teamId, setTeamId] = useState("");
  const [role, setRole] = useState<MemberRole>("member");

  const submitDisabled =
    isCreatePending || email.trim().length === 0 || teamId.length === 0;

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmit({ email: email.trim(), member_role: role, team_id: teamId });
  }

  return (
    <Drawer ariaLabel="Invite" isOpen onDismiss={onDismiss}>
      <form onSubmit={handleSubmit}>
        <Stack gap={3}>
          <TextInput
            label="Email"
            required
            type="email"
            value={email}
            onChange={(event) => {
              setEmail(event.currentTarget.value);
            }}
          />
          <Select
            label="Team"
            required
            value={teamId}
            onChange={(event) => {
              setTeamId(event.currentTarget.value);
            }}
          >
            <option value="">Choose team</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.display_name}
              </option>
            ))}
          </Select>
          {isTeamsPending ? (
            <Text variant="muted">Loading teams...</Text>
          ) : null}
          {teamsErrorMessage ? (
            <InlineError>{teamsErrorMessage}</InlineError>
          ) : null}
          <Select
            label="Role"
            required
            value={role}
            onChange={(event) => {
              setRole(event.currentTarget.value as MemberRole);
            }}
          >
            {grantableRoles.map((memberRole) => (
              <option key={memberRole} value={memberRole}>
                {memberRole}
              </option>
            ))}
          </Select>
          <Inline gap={3}>
            <Button disabled={submitDisabled} type="submit">
              {isCreatePending ? "Inviting..." : "Send invitation"}
            </Button>
            <Button type="button" variant="secondary" onClick={onDismiss}>
              Cancel
            </Button>
          </Inline>
          {createErrorMessage ? (
            <InlineError>{createErrorMessage}</InlineError>
          ) : null}
        </Stack>
      </form>
    </Drawer>
  );
}
